import type { AxiosError } from 'axios';
import i18next from 'i18next';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

import {
  EnumOrganizationConfig,
  EnumOrganizationTerminology,
} from '@/helpers/enums/organization-config.enum';
import {
  AssessmentContextSchema,
  type OrganizationAssessments,
} from '@/helpers/interfaces/assessments.interface';
import type { OrganizationPages } from '@/helpers/interfaces/dynamic-pages.interface';
import type { LearningCategoryWithChild } from '@/helpers/interfaces/learningCategories.interface';
import { MsegatConfigurationValueSchema } from '@/helpers/interfaces/msegat.interface';
import type { OrganizationTerminology } from '@/helpers/interfaces/organization.term.interface';
import type { Paginated } from '@/helpers/interfaces/paginated.interface';
import { pinia } from '@/pinia';
import { axiosI } from '@/plugins';
import type { Media } from '@/submodules/generated_types/types/media';
import { Organization } from '@/submodules/generated_types/types/organization';
import { ApiHandler } from '@/submodules/postman-to-ts/apiHandler';

import { useLocaleStore } from './locale.store';

export const useOrganizationStore = defineStore('organization', () => {
  const organization: Ref<Organization | null> = ref(null);
  const organizationId: Ref<number | null> = ref(null);
  const organizationNotFound = ref(false);
  const twoFactorEnabled: Ref<boolean> = ref(false);
  const organizationAssessments: Ref<OrganizationAssessments[]> = ref([]);

  const organizationLearningCategories: Ref<
    Paginated<LearningCategoryWithChild>
  > = ref({
    data: [],
    count: 0,
  });
  const selfRegistrationEnabled: Ref<boolean> = ref(false);
  const registrationRequiresApproval: Ref<{
    domain: string;
    strategy: string;
  } | null> = ref(null);
  const marketplaceEnabled: Ref<boolean> = ref(false);
  const dynamicPagesEnabled: Ref<boolean> = ref(false);

  const selfEnrollmentEnabled: Ref<boolean> = ref(false);
  const allowAnonymousCourseView: Ref<boolean> = ref(false);
  const allowAnonymousExternalCourseView: Ref<boolean> = ref(false);
  const allowAnonymousEventView: Ref<boolean> = ref(false);
  const certificatesEnabled: Ref<boolean> = ref(false);
  const passingCriteriaConfigurationEnabledForAdmins: Ref<boolean> = ref(false);
  const passingCriteriaConfigurationEnabledForInstructors: Ref<boolean> =
    ref(false);
  const organizationPages: Ref<OrganizationPages | null> = ref(null);
  const moyasarEnabled: Ref<boolean> = ref(false);
  const tabbyEnabled: Ref<boolean> = ref(false);
  const tamaraEnabled: Ref<boolean> = ref(false);
  const msegatEnabled: Ref<boolean> = ref(false);
  const googleEnabled: Ref<boolean> = ref(false);
  const walletEnabled: Ref<boolean> = ref(false);
  const assessmentEnabled: Ref<boolean> = ref(false);
  const clarityProjectId: Ref<string> = ref('');
  const organizationTerminology: Ref<
    Record<string, OrganizationTerminology[]>
  > = ref({});
  const loginPageImage: Ref<string> = ref('');
  const loginOrganizationLogo: Ref<string> = ref('');
  const courseBannerImage: Ref<string> = ref('');
  const eventBannerImage: Ref<string> = ref('');
  const courseBannerTitle: Ref<string> = ref('');
  const courseBannerSubtitle: Ref<string> = ref('');
  const eventBannerTitle: Ref<string> = ref('');
  const eventBannerSubtitle: Ref<string> = ref('');
  const externalCourseBannerImage: Ref<string> = ref('');
  const externalCourseBannerTitle: Ref<string> = ref('');
  const externalCourseBannerSubtitle: Ref<string> = ref('');
  const { t } = i18next;
  const studentText: Ref<string> = ref('');
  const studentsText: Ref<string> = ref('');
  const moduleText: Ref<string> = ref('');
  const modulesText: Ref<string> = ref('');
  const instructorText: Ref<string> = ref('');
  const instructorsText: Ref<string> = ref('');
  const courseText: Ref<string> = ref('');
  const coursesText: Ref<string> = ref('');
  const blendedText: Ref<string> = ref('');
  const dashboardText: Ref<string> = ref('');
  const enrollmentTermAndConditions: Ref<string> = ref('');
  const grantingPhotoUsagePermissions: Ref<string> = ref('');
  const externalCoursesAccreditationNotice: Ref<string> = ref('');
  const callOrganization = async () => {
    try {
      const { path, method } = ApiHandler.auth.getOrganization;
      const { data } = await axiosI<Organization>(path(), {
        method,
      });
      organization.value = data;
      setOrganizationConfig();
    } catch (error) {
      console.log(error);
    }
  };

  const getOrganizationAssessment = async () => {
    try {
      const { path, method } =
        ApiHandler.organizations.getOrganizationAssessments;
      const { data } = await axiosI<Paginated<OrganizationAssessments>>(
        path(),
        {
          method,
        }
      );
      organizationAssessments.value = data.data.filter((assessment) => {
        if (
          assessment.context == AssessmentContextSchema.Enum.INTERESTED_TOPIC ||
          assessment.context == AssessmentContextSchema.Enum.SUB_TOPIC
        ) {
          return organizationLearningCategories.value?.data.length;
        } else {
          return assessment.options.length;
        }
      });
      return data.data;
    } catch (e) {
      return e;
    }
  };
  const getOrganizationLearningCategories = async (
    params: typeof ApiHandler.organizations.getParentLearningCategories.params
  ) => {
    try {
      const { path, method } =
        ApiHandler.organizations.getParentLearningCategories;
      const { data } = await axiosI(path(), {
        method,
        params,
      });
      organizationLearningCategories.value = data;
      return data;
    } catch (e) {
      organizationLearningCategories.value = {
        data: [],
        count: 0,
      };
    }
  };
  const getOrganizationUsingUrl = async () => {
    try {
      organizationNotFound.value = false;
      const { path, method } = ApiHandler.organizations.getIdFromUrl;
      const { data } = await axiosI<{
        id: number;
        terminology: Record<string, OrganizationTerminology[]>;
      }>(path(), {
        method,
      });
      localStorage.setItem('Organization', data.id.toString());
      organizationId.value = data.id;
      organizationTerminology.value = data.terminology;
      setOrganizationTerms();
      return data;
    } catch (error) {
      organizationNotFound.value = true;
    }
  };
  const setOrganizationConfig = () => {
    organization.value?.config.forEach((config) => {
      switch (config.configurationKey.key) {
        case EnumOrganizationConfig.TWO_FACTOR_ENABLED:
          twoFactorEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.SELF_REGISTRATION_ENABLED:
          selfRegistrationEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.REGISTRATION_REQUIRES_APPROVAL:
          registrationRequiresApproval.value = {
            domain:
              (
                config.value as {
                  domain: string;
                  strategy: string;
                }
              )?.domain || '',
            strategy:
              (
                config.value as {
                  domain: string;
                  strategy: string;
                }
              )?.strategy || '',
          };
          break;
        case EnumOrganizationConfig.MARKETPLACE_ENABLED:
          marketplaceEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.DYNAMIC_PAGES_ENABLED:
          dynamicPagesEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.SELF_ENROLLMENT_ENABLED:
          selfEnrollmentEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.ALLOW_ANONYMOUS_COURSE_VIEW:
          allowAnonymousCourseView.value = !!config.value;
          break;
        case EnumOrganizationConfig.ALLOW_ANONYMOUS_EXTERNAL_COURSE_VIEW:
          allowAnonymousExternalCourseView.value = !!config.value;
          break;
        case EnumOrganizationConfig.ALLOW_ANONYMOUS_EVENT_VIEW:
          allowAnonymousEventView.value = !!config.value;
          break;
        case EnumOrganizationConfig.CERTIFICATES_ENABLED:
          certificatesEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.PASSING_CRITERIA_CONFIGURATION_ENABLED_FOR_ADMINS:
          passingCriteriaConfigurationEnabledForAdmins.value = !!config.value;
          break;
        case EnumOrganizationConfig.PASSING_CRITERIA_CONFIGURATION_ENABLED_FOR_INSTRUCTORS:
          passingCriteriaConfigurationEnabledForInstructors.value =
            !!config.value;
          break;
        case EnumOrganizationConfig.MOYASAR_CREDENTIAL_CONFIGURATION:
          moyasarEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.TABBY_CREDENTIAL_CONFIGURATION:
          tabbyEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.TAMARA_CREDENTIAL_CONFIGURATION:
          tamaraEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.MSEGAT_CREDENTIAL_CONFIGURATION:
          msegatEnabled.value =
            MsegatConfigurationValueSchema.safeParse(config.value).success ==
            false
              ? false
              : !!MsegatConfigurationValueSchema.safeParse(config.value).data
                  ?.enableOtp;
          break;
        case EnumOrganizationConfig.GOOGLE_CREDENTIAL_CONFIGURATION:
          googleEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.WALLET_ENABLED:
          walletEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.ASSESSMENT_ENABLED:
          assessmentEnabled.value = !!config.value;
          break;
        case EnumOrganizationConfig.CLARITY_PROJECT_ID:
          clarityProjectId.value =
            (
              config.value as {
                project_id: string;
              }
            )?.project_id || '';
          break;
      }
    });
  };
  const getOrganizationTerminology = async () => {
    try {
      const { path, method } =
        ApiHandler.organizations.getOrganizationsTerminology;
      const { data } = await axiosI<Record<string, OrganizationTerminology[]>>(
        path(),
        { method }
      );
      organizationTerminology.value = data;
      setOrganizationTerms();
      return data;
    } catch (error) {
      return error;
    }
  };
  const getMediaValue: (
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => Media | undefined | null = (
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => {
    const locale = localeKey || (useLocaleStore(pinia).locale as 'ar' | 'en');
    if (term?.defaultValue?.isRequired) {
      let value: Media | undefined | null = undefined;
      term.organizationTerms?.forEach((termKey) => {
        if (termKey.language == locale) {
          value = termKey.medium || term.defaultValue.media || undefined;
        }
      });
      return value || undefined;
    } else {
      if (term?.organizationTerms?.length) {
        let value: Media | undefined | null = undefined;
        term.organizationTerms?.forEach((termKey) => {
          if (termKey.language == locale) {
            value = termKey.medium || undefined;
          }
        });
        return value || undefined;
      }
      // special case for login organization logo
      else if (
        term?.slug === EnumOrganizationTerminology.LOGIN_ORGANIZATION_LOGO
      )
        return organization.value?.logo;
      else return term?.defaultValue.media || undefined;
    }
  };
  const getValue: (
    isSingular: boolean,
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => string = (
    isSingular: boolean,
    term?: OrganizationTerminology,
    localeKey?: 'ar' | 'en'
  ) => {
    const locale = localeKey || (useLocaleStore(pinia).locale as 'ar' | 'en');
    if (term?.defaultValue?.isRequired) {
      if (!term.organizationTerms?.length) {
        return (
          (isSingular
            ? term.defaultValue[locale].singular
            : term.defaultValue[locale].plural) || ''
        );
      } else {
        let value = '';
        term.organizationTerms?.forEach((termKey) => {
          if (termKey.language == locale) {
            value =
              (isSingular ? termKey.value.singular : termKey.value.plural) ||
              (isSingular
                ? term.defaultValue[locale].singular
                : term.defaultValue[locale].plural) ||
              '';
          }
        });
        return value;
      }
    } else {
      if (term?.organizationTerms?.length) {
        let value = '';
        term.organizationTerms?.forEach((termKey) => {
          if (termKey.language == locale)
            value =
              (isSingular ? termKey.value.singular : termKey.value.plural) ||
              '';
        });
        return value;
      } else
        return (
          (isSingular
            ? term?.defaultValue[locale].singular
            : term?.defaultValue[locale].plural) || ''
        );
    }
  };
  const setOrganizationTerms = () => {
    /** >>>>>>>>>>>>>>> Login Page Image <<<<<<<<<<<<<<< **/
    loginPageImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.AUTH_IMAGE
        ]?.[0]
      )?.url || '';
    loginOrganizationLogo.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.LOGIN_ORGANIZATION_LOGO
        ]?.[0]
      )?.url || '';

    /** >>>>>>>>>>>>>>> External Course Banner <<<<<<<<<<<<<<< **/
    //>>>>>>> Image
    externalCourseBannerImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSE_HEADER_IMAGE
        ]?.[0]
      )?.url || '';
    //>>>>>>> Title

    externalCourseBannerTitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSE_BANNER_TITLE
        ]?.[0]
      ) || '';
    //>>>>>>> Subtitle
    externalCourseBannerSubtitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSE_BANNER_SUBTITLE
        ]?.[0]
      ) || '';
    /** >>>>>>>>>>>>>>> Course Banner <<<<<<<<<<<<<<< **/
    //>>>>>>> Image
    courseBannerImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.COURSE_HEADER_IMAGE
        ]?.[0]
      )?.url || '';
    //>>>>>>> Title

    courseBannerTitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.COURSE_BANNER_TITLE
        ]?.[0]
      ) || '';
    //>>>>>>> Subtitle
    courseBannerSubtitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.COURSE_BANNER_SUBTITLE
        ]?.[0]
      ) || '';
    /** >>>>>>>>>>>>>>> Event Banner <<<<<<<<<<<<<<< **/
    //>>>>>>> Image
    eventBannerImage.value =
      getMediaValue(
        organizationTerminology.value[
          EnumOrganizationTerminology.EVENT_HEADER_IMAGE
        ]?.[0]
      )?.url || '';
    //>>>>>>> Title
    eventBannerTitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EVENT_BANNER_TITLE
        ]?.[0]
      ) || '';
    //>>>>>>> Subtitle
    eventBannerSubtitle.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EVENT_BANNER_SUBTITLE
        ]?.[0]
      ) || '';
    /** >>>>>>>>>>>>>>> Text Keys <<<<<<<<<<<<<<< **/
    //>>>>>>> Blended
    blendedText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.BLENDED]?.[0]
      ) || '';
    //>>>>>>> Course
    courseText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.COURSE]?.[0]
      ) || '';
    coursesText.value =
      getValue(
        false,
        organizationTerminology.value[EnumOrganizationTerminology.COURSE]?.[0]
      ) || '';
    //>>>>>>> Dashboard
    dashboardText.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.DASHBOARD
        ]?.[0]
      ) || '';
    //>>>>>>> Instructor
    instructorText.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.INSTRUCTOR
        ]?.[0]
      ) || '';
    instructorsText.value =
      getValue(
        false,
        organizationTerminology.value[
          EnumOrganizationTerminology.INSTRUCTOR
        ]?.[0]
      ) || '';
    //>>>>>>> Module
    moduleText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.MODULE]?.[0]
      ) || '';
    modulesText.value =
      getValue(
        false,
        organizationTerminology.value[EnumOrganizationTerminology.MODULE]?.[0]
      ) || '';
    //>>>>>>> Student
    studentText.value =
      getValue(
        true,
        organizationTerminology.value[EnumOrganizationTerminology.STUDENT]?.[0]
      ) || '';
    studentsText.value =
      getValue(
        false,
        organizationTerminology.value[EnumOrganizationTerminology.STUDENT]?.[0]
      ) || '';

    enrollmentTermAndConditions.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.ENROLLMENT_TERMS_AND_CONDITIONS
        ]?.[0]
      ) || '';
    grantingPhotoUsagePermissions.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.GRANTING_PHOTO_USAGE_PERMISSIONS
        ]?.[0]
      ) || '';
    externalCoursesAccreditationNotice.value =
      getValue(
        true,
        organizationTerminology.value[
          EnumOrganizationTerminology.EXTERNAL_COURSES_ACCREDITATION_NOTICE
        ]?.[0]
      ) || '';
  };
  const getOrganizationDynamicPages = async () => {
    try {
      const { path, method } = ApiHandler.dynamicPages.getOrganizationPages;
      const { data } = await axiosI<OrganizationPages>(path(), {
        method,
      });
      organizationPages.value = data;
      return data;
    } catch (error) {
      return error as AxiosError;
    }
  };
  return {
    organization,
    getOrganizationDynamicPages,
    organizationPages,
    organizationId,
    callOrganization,
    getOrganizationUsingUrl,
    twoFactorEnabled,
    getOrganizationLearningCategories,
    organizationLearningCategories,
    selfRegistrationEnabled,
    registrationRequiresApproval,
    marketplaceEnabled,
    dynamicPagesEnabled,
    selfEnrollmentEnabled,
    allowAnonymousCourseView,
    allowAnonymousExternalCourseView,
    allowAnonymousEventView,
    certificatesEnabled,
    passingCriteriaConfigurationEnabledForAdmins,
    passingCriteriaConfigurationEnabledForInstructors,
    moyasarEnabled,
    tabbyEnabled,
    tamaraEnabled,
    msegatEnabled,
    googleEnabled,
    walletEnabled,
    assessmentEnabled,
    clarityProjectId,
    getOrganizationTerminology,
    loginPageImage,
    courseBannerImage,
    eventBannerImage,
    courseBannerTitle,
    courseBannerSubtitle,
    eventBannerTitle,
    eventBannerSubtitle,
    studentText,
    studentsText,
    moduleText,
    modulesText,
    instructorText,
    instructorsText,
    courseText,
    coursesText,
    blendedText,
    dashboardText,
    setOrganizationTerms,
    loginOrganizationLogo,
    enrollmentTermAndConditions,
    grantingPhotoUsagePermissions,
    externalCoursesAccreditationNotice,
    externalCourseBannerImage,
    externalCourseBannerSubtitle,
    externalCourseBannerTitle,
    getOrganizationAssessment,
    organizationAssessments,
    organizationNotFound,
  };
});
